// AcceptTermsPage.jsx
// Shown once to every signed-in user who has not accepted the current Terms of Service
// and Privacy Policy — new sign-ups by any method, and existing users when LEGAL_VERSION
// changes. It sits BEFORE the pending-approval screen, so acceptance is recorded at
// sign-up rather than whenever an administrator gets round to approving.
//
// Styled as the pending screen (pa-*), for the same reason that screen mirrors the
// login page: someone who has just signed in should recognise where they are.

import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import LegalLinks from '../legal/LegalLinks';
import { erasureMode } from '../legal/legalConfig';
import './PendingApprovalPage.css';
import './AcceptTermsPage.css';

export default function AcceptTermsPage({ status }) {
  const { logout } = useAuth();
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');

  if (status.state === 'error') {
    return (
      <div className="pa-wrap">
        <div className="pa-card">
          <h1 className="pa-title">We couldn't load your account</h1>
          <p className="pa-body pa-body--muted">{status.error}</p>
          <button type="button" className="pa-btn" onClick={status.retry}>Try again</button>
          <button type="button" className="at-secondary" onClick={logout}>Sign out</button>
        </div>
      </div>
    );
  }

  async function submit(e) {
    e.preventDefault();
    if (!agreed) return;
    setBusy(true);
    setError('');
    const err = await status.accept();
    setBusy(false);
    if (err) { setError(err); setAgreed(false); }
  }

  return (
    <div className="pa-wrap">
      <form className="pa-card at-card" onSubmit={submit}>
        <h1 className="pa-title">Before you continue</h1>

        <p className="pa-body">
          Please read and accept our{' '}
          <a href="/#terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and{' '}
          <a href="/#privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        </p>

        <ul className="at-points">
          <li>Recommendations are an additional input to your own judgement — not the
            final word. You are responsible for the decisions you make.</li>
          <li>Information gathered from the internet or generated by AI may be inaccurate,
            and is not verified by us.</li>
          <li>Your data is not visible to other organisations. Members of your
            organisation and system administrators can see it.</li>
          <li>{erasureMode() === 'on-close'
            ? 'Your data is kept while your account is active, so the service can learn from it. When you close your account, you can ask for it to be deleted.'
            : 'You can ask for your data to be deleted at any time from Settings.'}</li>
        </ul>

        {error && <p className="at-error" role="alert">{error}</p>}

        <label className="at-check">
          <input
            type="checkbox"
            checked={agreed}
            onChange={e => setAgreed(e.target.checked)}
            disabled={busy}
          />
          <span>I have read and agree to the Terms of Service and Privacy Policy.</span>
        </label>

        <button type="submit" className="pa-btn" disabled={!agreed || busy}>
          {busy ? 'Saving…' : 'Agree and continue'}
        </button>
        <button type="button" className="at-secondary" onClick={logout} disabled={busy}>
          Sign out
        </button>

        <LegalLinks className="at-links" />
      </form>
    </div>
  );
}
